import React, {useCallback, useEffect, useState} from 'react';
import {
    Alert,
    Badge,
    Box,
    Breadcrumbs,
    Button,
    Card,
    CardActionArea,
    CardContent,
    Chip,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Drawer,
    FormControl,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    Link as MuiLink,
    List,
    ListItemButton,
    ListItemText,
    MenuItem,
    Select,
    Skeleton,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery
} from '@mui/material';
import {Link, useNavigate, useSearchParams} from 'react-router-dom';
import {useTheme} from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AddIcon from '@mui/icons-material/Add';
import {Item} from '../types/item';
import {Category, getCategoryById} from '../helpers/categories';
import {getUser, removeUser} from '../helpers/crypto';
import {addToCart, getCartCount} from '../helpers/cart';
import {Constants} from '../components/Constants';
import {iconNameToIcon} from '../components/iconNameToIcon';

const ICON_OPTIONS = ['standartCuff', 'bigCuff', 'bulbs', 'adapters', 'connectors', 'others'];

const NO_IMAGE = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="160" viewBox="0 0 200 160">' +
    '<rect fill="%23f0f4f8" width="200" height="160"/>' +
    '<text fill="%23aab" font-size="13" font-family="sans-serif" x="50%25" y="50%25" text-anchor="middle" dy=".3em">Без фото</text>' +
    '</svg>';

function getImages(item: Item): string[] {
    const imgs = [item.imgPath1, item.imgPath2, item.imgPath3].filter(Boolean) as string[];
    return imgs.length > 0 ? imgs : [NO_IMAGE];
}

const ItemImageCarousel: React.FC<{ item: Item }> = ({ item }) => {
    const images = getImages(item);
    const [index, setIndex] = useState(0);
    const hasMany = images.length > 1;

    const prev = (e: React.MouseEvent) => {
        e.stopPropagation();
        setIndex(i => (i - 1 + images.length) % images.length);
    };
    const next = (e: React.MouseEvent) => {
        e.stopPropagation();
        setIndex(i => (i + 1) % images.length);
    };

    return (
        <Box sx={{ position: 'relative', height: 180, bgcolor: '#fff', overflow: 'hidden', borderRadius: '12px 12px 0 0' }}>
            <Box
                component="img"
                src={images[index]}
                alt={item.name}
                onError={(e: any) => { e.target.src = NO_IMAGE; }}
                sx={{ width: '100%', height: '100%', objectFit: 'contain', p: 1, transition: 'opacity 0.2s' }}
            />

            {/* Prev / Next */}
            {hasMany && (
                <>
                    <IconButton
                        onClick={prev}
                        size="small"
                        sx={{
                            position: 'absolute', left: 4, top: '50%', transform: 'translateY(-50%)',
                            bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: '#fff' },
                            boxShadow: '0 1px 4px rgba(0,0,0,0.15)', p: 0.5
                        }}
                    >
                        <ChevronLeftIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        onClick={next}
                        size="small"
                        sx={{
                            position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)',
                            bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: '#fff' },
                            boxShadow: '0 1px 4px rgba(0,0,0,0.15)', p: 0.5
                        }}
                    >
                        <ChevronRightIcon fontSize="small" />
                    </IconButton>
                </>
            )}

            {/* Dots */}
            {hasMany && (
                <Box sx={{ position: 'absolute', bottom: 6, width: '100%', display: 'flex', justifyContent: 'center', gap: 0.75 }}>
                    {images.map((_, i) => (
                        <Box
                            key={i}
                            onClick={(e) => { e.stopPropagation(); setIndex(i); }}
                            sx={{
                                width: i === index ? 16 : 6, height: 6, borderRadius: 3,
                                bgcolor: i === index ? '#0077CC' : 'rgba(0,0,0,0.25)',
                                cursor: 'pointer', transition: 'all 0.2s'
                            }}
                        />
                    ))}
                </Box>
            )}
        </Box>
    );
};

const CatalogPage: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [searchParams, setSearchParams] = useSearchParams();

    const [items, setItems] = useState<Item[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [search, setSearch] = useState(searchParams.get('search') || '');
    const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
    const [activeCategory, setActiveCategory] = useState<number>(Number(searchParams.get('category')) || 0);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [user, setUser] = useState<any>(null);
    const [cartCount, setCartCount] = useState(0);

    const [categories, setCategories] = useState<Category[]>([]);
    const [catDialogOpen, setCatDialogOpen] = useState(false);
    const [catForm, setCatForm] = useState({ id: 0, title: '', description: '', iconame: '' });
    const [catSaving, setCatSaving] = useState(false);
    const [catError, setCatError] = useState('');

    useEffect(() => {
        setUser(getUser());
        setCartCount(getCartCount());
        fetchCategories();
    }, []);

    const fetchCategories = async () => {
        try {
            const res = await fetch(`${Constants.SERVER_URL}${Constants.GET_CATEGORIES_PREFIX}`);
            const data = await res.json();
            if (data.success) setCategories(data.categories);
        } catch { /* silent */ }
    };

    const openCatDialog = (cat?: Category) => {
        setCatError('');
        if (cat) {
            setCatForm({ id: cat.id, title: cat.title, description: cat.description, iconame: cat.iconame });
        } else {
            setCatForm({ id: 0, title: '', description: '', iconame: ICON_OPTIONS[0] });
        }
        setCatDialogOpen(true);
    };

    const saveCat = async () => {
        if (!catForm.title.trim()) { setCatError('Введіть назву категорії'); return; }
        setCatSaving(true);
        setCatError('');
        try {
            const res = await fetch(`${Constants.SERVER_URL}${Constants.SAVE_CATEGORY_PREFIX}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(catForm),
            });
            const data = await res.json();
            if (data.success) {
                await fetchCategories();
                setCatDialogOpen(false);
            } else {
                setCatError(data.error || 'Помилка збереження');
            }
        } catch { setCatError('Помилка з\'єднання'); }
        finally { setCatSaving(false); }
    };

    const handleAddToCart = (item: Item) => {
        if (!user) {
            navigate('/login');
            return;
        }
        addToCart(item);
        setCartCount(getCartCount());
    };

    const handleLogout = () => {
        removeUser();
        setUser(null);
    };

    const fetchItems = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const params = new URLSearchParams();
            if (activeCategory > 0) params.set('category', String(activeCategory));
            if (search.trim()) params.set('search', search.trim());
            const currentUser = getUser();
            params.set('status', currentUser?.status || 'REGULAR');

            const res = await fetch(`${Constants.SERVER_URL}${Constants.GET_ITEMS_PREFIX}?${params}`);
            const data = await res.json();
            if (data.success) {
                setItems(data.items);
            } else {
                setError(data.error || 'Помилка завантаження товарів');
            }
        } catch {
            setError("Помилка з'єднання з сервером");
        } finally {
            setLoading(false);
        }
    }, [activeCategory, search]);

    useEffect(() => {
        void fetchItems();
        const params: Record<string, string> = {};
        if (activeCategory > 0) params.category = String(activeCategory);
        if (search.trim()) params.search = search.trim();
        setSearchParams(params);
    }, [activeCategory, search, fetchItems, setSearchParams]);

    const handleSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setSearch(searchInput);
    };

    const handleCategorySelect = (id: number) => {
        setActiveCategory(id);
        setDrawerOpen(false);
    };

    const currentCategory = getCategoryById(activeCategory, categories);

    const isAdmin = user?.status === 'ADMIN';

    const CategoryList = () => (
        <Box>
            <ListItemButton
                selected={activeCategory === 0}
                onClick={() => handleCategorySelect(0)}
                sx={{ borderRadius: 2, mb: 0.5, '&.Mui-selected': { bgcolor: '#e6f2ff', color: '#0077CC' } }}
            >
                <ListItemText primary="Всі категорії"
                              slotProps={{ primary: { sx: { fontWeight: activeCategory === 0 ? 600 : 400 } } }} />
            </ListItemButton>
            <Divider sx={{ my: 1 }} />
            {categories.map(cat => (
                <Box key={cat.id} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <ListItemButton
                        selected={activeCategory === cat.id}
                        onClick={() => handleCategorySelect(cat.id)}
                        sx={{ borderRadius: 2, flexGrow: 1, '&.Mui-selected': { bgcolor: '#e6f2ff', color: '#0077CC' } }}
                    >
                        <ListItemText
                            primary={cat.title}
                            secondary={cat.description}
                            slotProps={{ primary: { sx: { fontWeight: activeCategory === cat.id ? 600 : 400, fontSize: 14 } }
                                , secondary: { sx: { fontSize: 12 } } }}
                        />
                    </ListItemButton>
                    {isAdmin && (
                        <Tooltip title="Редагувати">
                            <IconButton size="small" onClick={() => openCatDialog(cat)} sx={{ ml: 0.5 }}>
                                <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            ))}
            {isAdmin && (
                <Button
                    size="small" startIcon={<AddIcon />} fullWidth
                    sx={{ mt: 1, textTransform: 'none', color: '#0077CC' }}
                    onClick={() => openCatDialog()}
                >
                    Додати категорію
                </Button>
            )}
        </Box>
    );

    return (
        <Box sx={{ bgcolor: '#f7f9fc', minHeight: '100vh' }}>
            {/* Header */}
            <Box sx={{ position: 'sticky', top: 0, bgcolor: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)'
                , borderBottom: '1px solid #e0e6ed', zIndex: 100 }}>
                <Container maxWidth="lg">
                    <Box sx={{ display: 'flex', alignItems: 'center', height: 70, gap: 2 }}>
                        <Box component={Link} to="/"
                             sx={{ display: 'flex', alignItems: 'center', gap: 1, textDecoration: 'none', flexShrink: 0 }}>
                            <LocalHospitalIcon sx={{ color: '#0077CC', fontSize: 28 }} />
                            <Typography sx={{ fontWeight: 700, fontSize: 20, color: '#1a1a2e' }}>{Constants.APP_LABEL}</Typography>
                        </Box>

                        {/* Search */}
                        <Box component="form" onSubmit={handleSearchSubmit} sx={{ flexGrow: 1, mx: { xs: 1, md: 3 } }}>
                            <TextField
                                fullWidth size="small" placeholder="Пошук товарів..."
                                value={searchInput} onChange={e => setSearchInput(e.target.value)}
                                sx={{ bgcolor: '#f7f9fc', borderRadius: 2 }}
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon sx={{ color: '#999' }} />
                                            </InputAdornment>
                                        ),
                                        endAdornment: searchInput ? (
                                            <InputAdornment position="end">
                                                <IconButton size="small" onClick={() => { setSearchInput('');
                                                    setSearch(''); }}>
                                                    <CloseIcon fontSize="small" />
                                                </IconButton>
                                            </InputAdornment>
                                        ) : null
                                    }
                                }}
                            />
                        </Box>

                        {/* User actions */}
                        {!isMobile && (
                            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexShrink: 0 }}>
                                {/* Cart icon */}
                                <IconButton onClick={() => user ? navigate('/cart') : navigate('/login')} sx={{ position: 'relative' }}>
                                    <Badge badgeContent={cartCount > 0 ? cartCount : null} color="primary">
                                        <ShoppingCartOutlinedIcon />
                                    </Badge>
                                </IconButton>
                                {user ? (
                                    <>
                                        <Badge
                                            badgeContent={user.status !== 'REGULAR' ? React.cloneElement(iconNameToIcon[user.status]) : null}
                                            sx={{ '& .MuiBadge-badge': { minWidth: 20, height: 20, p: '2px' } }}
                                        >
                                            <Button sx={{ textTransform: 'none', color: '#555', fontWeight: 500 }}
                                                    onClick={() => navigate('/profile')}>
                                                {user.name} {user.last_name}
                                            </Button>
                                        </Badge>
                                        <Button variant="outlined" size="small"
                                                sx={{ textTransform: 'none', borderRadius: 2 }}
                                                onClick={handleLogout}>Вийти</Button>
                                    </>
                                ) : (
                                    <>
                                        <Button variant="outlined" size="small"
                                                sx={{ textTransform: 'none', borderRadius: 2 }}
                                                onClick={() => navigate('/login')}>Увійти</Button>
                                        <Button variant="contained"
                                                size="small"
                                                sx={{ textTransform: 'none', borderRadius: 2 }}
                                                onClick={() => navigate('/register')}>Реєстрація</Button>
                                    </>
                                )}
                            </Box>
                        )}

                        {isMobile && (
                            <>
                                <IconButton onClick={() => user ? navigate('/cart') : navigate('/login')}>
                                    <Badge badgeContent={cartCount > 0 ? cartCount : null} color="primary">
                                        <ShoppingCartOutlinedIcon />
                                    </Badge>
                                </IconButton>
                                <IconButton onClick={() => setDrawerOpen(true)}>
                                    <FilterListIcon />
                                </IconButton>
                                <IconButton onClick={() => setMenuOpen(true)}>
                                    <MenuIcon />
                                </IconButton>
                            </>
                        )}
                    </Box>
                </Container>
            </Box>

            {/* Mobile filter drawer */}
            <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
                <Box sx={{ width: 280, p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: 16 }}>Категорії</Typography>
                        <IconButton onClick={() => setDrawerOpen(false)}><CloseIcon /></IconButton>
                    </Box>
                    <List disablePadding><CategoryList /></List>
                </Box>
            </Drawer>

            {/* Mobile menu drawer */}
            <Drawer anchor="right" open={menuOpen} onClose={() => setMenuOpen(false)}>
                <Box sx={{ width: 240, p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                        <IconButton onClick={() => setMenuOpen(false)}><CloseIcon /></IconButton>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {user ? (
                            <>
                                <Badge
                                    badgeContent={user.status !== 'REGULAR' ? React.cloneElement(iconNameToIcon[user.status]) : null}
                                    sx={{ '& .MuiBadge-badge': { minWidth: 20, height: 20, p: '2px' } }}
                                >
                                    <Button sx={{ textTransform: 'none', color: '#555', fontWeight: 500 }}
                                            onClick={() => navigate('/profile')}>
                                        {user.name} {user.last_name}
                                    </Button>
                                </Badge>
                                <Button variant="outlined" fullWidth
                                        sx={{ textTransform: 'none', borderRadius: 2 }}
                                        onClick={() => { setMenuOpen(false); handleLogout(); }}>Вийти</Button>
                            </>
                        ) : (
                            <>
                                <Button variant="outlined" fullWidth
                                        sx={{ textTransform: 'none', borderRadius: 2 }}
                                        onClick={() => { setMenuOpen(false); navigate('/login'); }}>Увійти</Button>
                                <Button variant="contained" fullWidth
                                        sx={{ textTransform: 'none', borderRadius: 2 }}
                                        onClick={() => { setMenuOpen(false); navigate('/register'); }}>Реєстрація</Button>
                            </>
                        )}
                    </Box>
                </Box>
            </Drawer>

            <Container maxWidth="lg" sx={{ py: 3 }}>
                {/* Breadcrumbs */}
                <Breadcrumbs sx={{ mb: 2.5, fontSize: 14 }}>
                    <MuiLink component={Link} to="/" underline="hover" color="inherit">Головна</MuiLink>
                    <Typography color="text.primary" sx={{ fontSize: 14}}>Каталог</Typography>
                    {currentCategory && <Typography color="text.primary" sx={{ fontSize: 14}}>{currentCategory.title}</Typography>}
                </Breadcrumbs>

                <Grid container spacing={3}>
                    {/* Sidebar — desktop */}
                    {!isMobile && (
                        <Grid size={{ md: 3 }}>
                            <Box sx={{ bgcolor: '#fff', border: '1px solid #e0e6ed', borderRadius: 3, p: 2, position: 'sticky', top: 90 }}>
                                <Typography sx={{ fontWeight: 700, mb: 1.5, fontSize: 15 }}>Категорії</Typography>
                                <List disablePadding><CategoryList /></List>
                            </Box>
                        </Grid>
                    )}

                    {/* Items grid */}
                    <Grid size={{ xs: 12, md: 9 }}>
                        {/* Title & count */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography sx={{ fontWeight: 700, fontSize: 20 }}>
                                {currentCategory ? currentCategory.title : 'Всі товари'}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            {user?.status === 'ADMIN' && (
                                <Button variant="contained" size="small"
                                    sx={{ textTransform: 'none', borderRadius: 2 }}
                                    onClick={() => navigate('/item')}>
                                    + Додати товар
                                </Button>
                            )}
                            {!loading && (
                                <Typography sx={{ color: '#555', fontSize: 14 }}>
                                    {items.length} {items.length === 1 ? 'товар' : items.length < 5 ? 'товари' : 'товарів'}
                                </Typography>
                            )}
                            </Box>
                        </Box>

                        {/* Active filters */}
                        {(activeCategory > 0 || search) && (
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                                {activeCategory > 0 && (
                                    <Chip
                                        label={currentCategory?.title}
                                        onDelete={() => setActiveCategory(0)}
                                        size="small" color="primary" variant="outlined"
                                    />
                                )}
                                {search && (
                                    <Chip
                                        label={`Пошук: ${search}`}
                                        onDelete={() => { setSearch(''); setSearchInput(''); }}
                                        size="small" color="primary" variant="outlined"
                                    />
                                )}
                            </Box>
                        )}

                        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                        {/* Skeletons */}
                        {loading && (
                            <Grid container spacing={2}>
                                {[...Array(6)].map((_, i) => (
                                    <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={i}>
                                        <Card elevation={0} sx={{ border: '1px solid #e0e6ed', borderRadius: 3 }}>
                                            <Skeleton variant="rectangular" height={160} />
                                            <CardContent>
                                                <Skeleton variant="text" width="60%" />
                                                <Skeleton variant="text" />
                                                <Skeleton variant="text" width="40%" />
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        )}

                        {/* Empty state */}
                        {!loading && !error && items.length === 0 && (
                            <Box sx={{ textAlign: 'center', py: 8 }}>
                                <SearchIcon sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
                                <Typography sx={{ fontWeight: 600, mb: 1 }}>Товари не знайдені</Typography>
                                <Typography sx={{ color: '#555', mb: 3 }}>Спробуйте змінити параметри пошуку</Typography>
                                <Button variant="outlined"
                                        sx={{ textTransform: 'none', borderRadius: 2 }}
                                        onClick={() => { setActiveCategory(0); setSearch('');
                                            setSearchInput(''); }}>
                                    Скинути фільтри
                                </Button>
                            </Box>
                        )}

                        {/* Items */}
                        {!loading && items.length > 0 && (
                            <Grid container spacing={2}>
                                {items.map(item => (
                                    <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={item.id}>
                                        <Card elevation={0} sx={{
                                            border: '1px solid #e0e6ed', borderRadius: 3, height: '100%',
                                            display: 'flex', flexDirection: 'column',
                                            transition: '0.25s',
                                            '&:hover': { boxShadow: '0 8px 30px rgba(0,0,0,0.1)'
                                                , transform: 'translateY(-3px)', borderColor: '#0077CC' }
                                        }}>
                                            <CardActionArea sx={{ flexGrow: 1, display: 'flex'
                                                , flexDirection: 'column', alignItems: 'stretch' }}>
                                                <ItemImageCarousel item={item} />
                                                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                                                    {/* Short name */}
                                                    <Typography sx={{ fontSize: 12, color: '#999', mb: 0.5 }}>
                                                        {item.shortName}
                                                    </Typography>
                                                    {/* Name */}
                                                    <Typography sx={{ fontWeight: 600, fontSize: 15, mb: 1
                                                        , lineHeight: 1.4, flexGrow: 1 }}>
                                                        {item.name}
                                                    </Typography>
                                                    {/* Description */}
                                                    {item.description && (
                                                        <Typography sx={{ fontSize: 13, color: '#555', mb: 1.5, lineHeight: 1.5,
                                                            display: '-webkit-box', WebkitLineClamp: 2
                                                            , WebkitBoxOrient: 'vertical', overflow: 'hidden'
                                                        }}>
                                                            {item.description}
                                                        </Typography>
                                                    )}
                                                    {/* Stock */}
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
                                                        {item.onStock ? (
                                                            <><CheckCircleIcon sx={{ fontSize: 15, color: '#2e7d32' }} />
                                                                <Typography sx={{ fontSize: 12, color: '#2e7d32' }}>
                                                                    В наявності</Typography></>
                                                        ) : (
                                                            <><CancelIcon sx={{ fontSize: 15, color: '#c62828' }} />
                                                                <Typography sx={{ fontSize: 12, color: '#c62828' }}>
                                                                    Немає в наявності</Typography></>
                                                        )}
                                                    </Box>
                                                    {/* Price */}
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        {item.priceAkcia > 0 ? (
                                                            <>
                                                                <LocalOfferIcon sx={{ fontSize: 16, color: '#e53935' }} />
                                                                <Typography sx={{ fontWeight: 700, fontSize: 18, color: '#e53935' }}>
                                                                    {item.priceAkcia.toFixed(2)} грн
                                                                </Typography>
                                                                <Typography
                                                                    sx={{ fontSize: 13, color: '#999', textDecoration: 'line-through' }}>
                                                                    {item.price.toFixed(2)}
                                                                </Typography>
                                                            </>
                                                        ) : (
                                                            <Typography sx={{ fontWeight: 700, fontSize: 18, color: '#0077CC' }}>
                                                                {item.price.toFixed(2)} грн
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </CardContent>
                                            </CardActionArea>
                                            {/* Add to cart / Admin edit */}
                                            <Box sx={{ p: 2, pt: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                <Button
                                                    variant="contained" fullWidth
                                                    disabled={!item.onStock}
                                                    startIcon={<ShoppingCartOutlinedIcon />}
                                                    sx={{ textTransform: 'none', borderRadius: 2 }}
                                                    onClick={() => handleAddToCart(item)}
                                                >
                                                    {item.onStock ? (user ? 'Додати в кошик' : 'Увійдіть для замовлення') : 'Немає в наявності'}
                                                </Button>
                                                {user?.status === 'ADMIN' && (
                                                    <Button variant="outlined" fullWidth size="small"
                                                        sx={{ textTransform: 'none', borderRadius: 2, fontSize: 12 }}
                                                        onClick={() => navigate(`/item?id=${item.id}`)}>
                                                        Редагувати
                                                    </Button>
                                                )}
                                            </Box>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </Container>

            {/* Category add/edit dialog (ADMIN only) */}
            <Dialog open={catDialogOpen} onClose={() => setCatDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>
                    {catForm.id ? 'Редагувати категорію' : 'Нова категорія'}
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
                    {catError && <Alert severity="error" onClose={() => setCatError('')}>{catError}</Alert>}
                    <TextField
                        label="Назва *" fullWidth value={catForm.title}
                        onChange={e => setCatForm(f => ({ ...f, title: e.target.value }))}
                    />
                    <TextField
                        label="Опис" fullWidth value={catForm.description}
                        onChange={e => setCatForm(f => ({ ...f, description: e.target.value }))}
                    />
                    <FormControl fullWidth>
                        <InputLabel>Іконка</InputLabel>
                        <Select
                            label="Іконка"
                            value={catForm.iconame}
                            onChange={e => setCatForm(f => ({ ...f, iconame: e.target.value }))}
                        >
                            {ICON_OPTIONS.map(name => (
                                <MenuItem key={name} value={name}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Box sx={{ color: '#0077CC', display: 'flex' }}>{iconNameToIcon[name]}</Box>
                                        <Typography sx={{ fontSize: 14 }}>{name}</Typography>
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setCatDialogOpen(false)} sx={{ textTransform: 'none' }}>Скасувати</Button>
                    <Button variant="contained" onClick={saveCat} disabled={catSaving}
                        sx={{ textTransform: 'none' }}>
                        {catSaving ? 'Збереження...' : 'Зберегти'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CatalogPage;
